import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { goApi } from '../../../services';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Typography,
    Paper,
    TablePagination,
    TextField,
    InputAdornment,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Button,
    Switch,
    FormControlLabel,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { format } from 'date-fns';
import UserInfo from '../../../common/UserInfo';
import { useRefresh } from 'react-admin';

const BlockedStatusField = ({
    source,
    value,
    statId,
    onToggle,
}: {
    source: string;
    value: boolean;
    statId: string;
    onToggle: (source: string, newValue: boolean) => void;
}) => {
    const label = source?.split('_')[0];
    const displayLabel = label.charAt(0).toUpperCase() + label.slice(1);

    return (
        <div className="flex items-center gap-2">
            <FormControlLabel
                control={
                    <Switch
                        checked={!value}
                        onChange={e => onToggle(source, !e.target.checked)}
                        color="success"
                        size="small"
                    />
                }
                label={
                    <span className={`text-sm ${!value ? 'text-green-700' : 'text-red-500'}`}>
                        {displayLabel}
                    </span>
                }
            />
        </div>
    );
};
const BatchStudentJoined = ({ schedule }: {
    schedule: any;
}) => {
    const [watchStatData, setWatchStatData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');
    const { id } = useParams();
    const refresh = useRefresh();

    const fetchScheduleContent = async () => {
        setLoading(true);
        try {
            const res = await goApi.get(`/watch-stats/${id}/stats?type=SCHEDULE`);
            setWatchStatData(res?.data || []);
        } catch (err) {
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchScheduleContent();
    }, [id]);

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const filteredData = watchStatData.filter((stat: any) => {
        if (!searchTerm) return true;
        const search = searchTerm.toLowerCase();
        const fullName = `${stat.user?.firstName || ''} ${stat.user?.lastName || ''}`.toLowerCase();
        const phone = stat.user?.phone || '';
        return fullName.includes(search) || phone.includes(search);
    });

    const paginatedData = filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    const formatDuration = (seconds: number) => {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const secs = seconds % 60;
        return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(
            secs,
        ).padStart(2, '0')}`;
    };

    const handleToggleBlock = async (statId: string, source: string, newValue: boolean) => {
        try {
            console.log(`Toggling ${source} for stat ID ${statId} to ${newValue}`);
            // await goApi.patch(`/watch-stats/${statId}`, {
            //     [source]: newValue,
            // });
            refresh();
            fetchScheduleContent();
        } catch (error) {
            console.error('Error updating block status:', error);
        }
    };

    return (
        <div className="w-full" style={{ overflow: 'hidden' }}>
            <div style={{ marginBottom: '1rem' }}>
                <TextField
                    fullWidth
                    variant="outlined"
                    placeholder="Search by student name or phone..."
                    value={searchTerm}
                    onChange={e => {
                        setSearchTerm(e.target.value);
                        setPage(0);
                    }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                    }}
                />
            </div>
            {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
                    <CircularProgress />
                </div>
            ) : watchStatData.length === 0 ? (
                <Typography
                    variant="body1"
                    color="text.secondary"
                    style={{ padding: '2rem', textAlign: 'center' }}
                >
                    No watch statistics available
                </Typography>
            ) : (
                <TableContainer component={Paper} elevation={1}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>
                                    <strong>Student</strong>
                                </TableCell>
                                <TableCell>
                                    <strong>Duration Watched</strong>
                                </TableCell>
                                <TableCell>
                                    <strong>Playback Allowed</strong>
                                </TableCell>
                                <TableCell>
                                    <strong>Playback Watched</strong>
                                </TableCell>
                                <TableCell>
                                    <strong>Created At</strong>
                                </TableCell>

                                <TableCell>
                                    <strong>Actions</strong>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {paginatedData.map((stat: any) => (
                                <TableRow key={stat?.id} hover>
                                    <TableCell>
                                        <UserInfo user={stat?.user} />
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={formatDuration(stat.durationWatchedInSeconds)}
                                            color="success"
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={schedule.restrictionMaxDurationMultiple}
                                            color="default"
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={stat.currentPlaybacks}
                                            color="success"
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <div className="flex items-center space-x-2">
                                            <div className="w-4 h-4 flex flex-col items-center rounded-full bg-amber-200">
                                                C
                                            </div>
                                            <div className="whitespace-nowrap">
                                                {format(
                                                    new Date(stat?.createdAt),
                                                    'dd-MMM-yyyy hh:mm a',
                                                )}
                                            </div>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                            <div className="w-4 h-4 flex flex-col items-center rounded-full bg-pink-200">
                                                U
                                            </div>
                                            <div className="whitespace-nowrap">
                                                {format(
                                                    new Date(stat?.updatedAt),
                                                    'dd-MMM-yyyy hh:mm a',
                                                )}
                                            </div>
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        <div className="flex flex-col gap-2">
                                            <BlockedStatusField
                                                source="chatBlocked"
                                                value={stat?.chatBlocked}
                                                statId={stat?.id}
                                                onToggle={(source, newValue) =>
                                                    handleToggleBlock(stat?.id, source, newValue)
                                                }
                                            />
                                            <BlockedStatusField
                                                source="accessBlocked"
                                                value={stat?.accessBlocked}
                                                statId={stat?.id}
                                                onToggle={(source, newValue) =>
                                                    handleToggleBlock(stat?.id, source, newValue)
                                                }
                                            />
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25, 50]}
                        component="div"
                        count={filteredData.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                </TableContainer>
            )}
        </div>
    );
};

export default BatchStudentJoined;
